import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  useOverlayContext,
  useMapContext,
  useActiveHexContext,
} from 'shared/context';
import { useGrid, useHexAnalysis, useMouseCoords } from 'shared/hooks';
import { GRID, HEX, ZINDEX } from 'shared/constants';

export const CanvasGrid = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { map } = useMapContext();
  const { setIsBigHexSelected, isBigHexViewActive, setIsBigHexViewActive } =
    useActiveHexContext();
  const {
    ctx,
    setCtx,
    isHexClickBlocked,
    setIsHexClickBlocked,
    hoveredHexPath,
  } = useOverlayContext();
  const { mouseX, mouseY } = useMouseCoords();
  const { getHexAnalysis, getPathOfHex, getHexCenterCoords } = useHexAnalysis();
  const { handleGridChange } = useGrid();
  const [isDrag, setIsDrag] = useState<boolean>(false);

  const shouldCanvasRender = map && GRID.MAX_ZOOM <= map.getZoom();

  const zoomOnHex = (hexPath: string) => {
    const hexCenter = getHexCenterCoords(hexPath);
    if (hexCenter) {
      map?.flyTo(hexCenter, HEX.BIG_HEX_ZOOM);
    }
  };

  const onCanvasClick = async (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isHexClickBlocked && shouldCanvasRender) {
      if (isBigHexViewActive) setIsBigHexViewActive(false);
      setIsBigHexSelected(true);
      const mouseX = event.clientX;
      const mouseY = event.clientY;
      const hexPath = getPathOfHex(mouseX, mouseY);
      if (!hexPath) return;
      zoomOnHex(hexPath);
      getHexAnalysis(hexPath);
    }
  };

  const onCanvasHover = () => {
    if (isHexClickBlocked || !ctx) return;
    if (hoveredHexPath && ctx.isPointInPath(hoveredHexPath, mouseX, mouseY))
      return;
    handleGridChange();
  };

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const context = canvas.getContext('2d');
    if (context !== null) {
      setCtx(context);
    }
  }, []);

  return (
    <Canvas
      ref={canvasRef}
      onMouseDown={() => {
        setIsDrag(true);
      }}
      onMouseMove={() => {
        onCanvasHover();
        if (isDrag && !isHexClickBlocked) {
          setIsHexClickBlocked(true);
        }
      }}
      onMouseUp={(event: React.MouseEvent<HTMLCanvasElement>) => {
        isHexClickBlocked ? setIsHexClickBlocked(false) : onCanvasClick(event);
        setIsDrag(false);
      }}
    />
  );
};

const Canvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  z-index: ${ZINDEX.CANVAS_GRID};
  overflow: hidden;
`;
